import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { connect } from "react-redux"

import { trackSlide, updateChoice } from "areas/product/store/actions"
import {
  ChoiceOption,
  GradientContainer,
  OptionTitle,
  OptionPrice,
  OptionsSlider,
} from "areas/product/wizard-options/components"
import Divider from "components/ui/divider"
import Image from "components/ui/image"
import { to_currency } from "helpers/number"
import { imageLookup } from "helpers/product/image-lookup"
import { filterProductOptionsV2 } from "helpers/product/product-filtering"
import { useNavigation } from "helpers/use-navigation"
import { cn } from "styles/utils"

const ChoiceOptionWrap = ({
  className = "",
  inModal = false,
  selected = false,
  ...props
}) => (
  <ChoiceOption
    aria-checked={selected}
    className={cn(
      "tablet:mr-0 tablet:mb-12 basis-124 mr-16 border",
      inModal ? "mb-12" : "mb-24",
      selected && "border-selected border-2",
      className
    )}
    {...props}
  />
)

ChoiceOptionWrap.propTypes = {
  className: PropTypes.string,
  inModal: PropTypes.bool,
  selected: PropTypes.bool,
}

const ChainType = ({
  chainTypes,
  inModal,
  selectedMetalFilter,
  selectedType,
  setChoice,
  setType,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  return (
    <OptionsSlider
      aria-label="Chain Type Options"
      className={cn(
        "tablet:grid",
        inModal ? "grid-cols-5 gap-10" : "grid-cols-3 gap-x-16"
      )}
      data-slider="true"
      role="radiogroup"
    >
      {chainTypes.map((type) => {
        const img = imageLookup(
          "chainType",
          `${type.imageKey}_${selectedMetalFilter.value.slice(-2)}`,
          "retina"
        )
        const selected = selectedType.label === type.label

        return (
          <ChoiceOptionWrap
            data-cy={`product-wizard-ch-type-${type.label}`}
            data-is-option="true"
            data-is-selected={selected}
            inModal={inModal}
            key={`chaintype-${type.label}`}
            onClick={() => {
              setType(type)
              setChoice({
                chain_type: type.type,
                display_price: null,
                imageKey: "",
                price: 0,
                title: null,
                value: "",
              })
            }}
            role="radio"
            selected={selected}
            tabIndex={selected ? 0 : -1}
            {...keyboardProps}
          >
            <Image
              alt={type.label}
              className="h-auto w-full"
              height="200"
              src={img.x2}
              srcSet={img.x1 + " 1x," + img.x2 + " 2x"}
              width="200"
            />
            <OptionTitle
              className={cn(
                "text-14 leading-(--base-leading) font-bold",
                inModal ? "tablet:text-14" : "tablet:text-16"
              )}
              style={{ "--base-leading": inModal ? "1.1" : "1.2" }}
            >
              {type.label}
            </OptionTitle>
          </ChoiceOptionWrap>
        )
      })}
    </OptionsSlider>
  )
}
ChainType.propTypes = {
  chainTypes: PropTypes.array,
  inModal: PropTypes.bool,
  selectedMetalFilter: PropTypes.object,
  selectedType: PropTypes.object,
  setChoice: PropTypes.func,
  setType: PropTypes.func,
}

const ChainLength = ({
  choices,
  inModal,
  selectedChoice,
  selectedType,
  setChoice,
}) => {
  const keyboardProps = useNavigation({ direction: "horizontal" })

  const chainLengthOptions = filterProductOptionsV2(
    selectedType.options,
    choices,
    true
  )

  useEffect(() => {
    if (chainLengthOptions.length === 1 && !selectedChoice.value) {
      setChoice(chainLengthOptions[0])
    }
  }, [chainLengthOptions, selectedChoice.value])

  return (
    <OptionsSlider
      aria-label="Chain Length Options"
      className={cn(
        "tablet:grid",
        inModal ? "grid-cols-5 gap-10" : "grid-cols-3 gap-x-16"
      )}
      data-slider="true"
      role="radiogroup"
    >
      {chainLengthOptions.map((chain, index) => {
        const img = imageLookup(
          "chainLength",
          `${chain.chain_figure || "woman"}_${chain.chain_length}`,
          "retina"
        )
        const selected = selectedChoice.value === chain.value
        return (
          <ChoiceOptionWrap
            data-cy={`product-wizard-ch-length-${chain.value}`}
            data-is-option="true"
            data-is-selected={selected}
            inModal={inModal}
            key={`chainlength-${chain.value}`}
            onClick={() => setChoice(chain)}
            role="radio"
            selected={selected}
            tabIndex={
              selected || (!selectedChoice.value && index === 0) ? 0 : -1
            }
            {...keyboardProps}
          >
            <Image
              alt={chain.description}
              className="h-auto w-full"
              height="136"
              src={img.x2}
              srcSet={img.x1 + " 1x," + img.x2 + " 2x"}
              width="136"
            />
            <OptionTitle className="text-14 mt-10 font-bold">
              {chain.chain_length_label}{" "}
              {chain.extender && (
                <span className="text-12 font-normal">
                  +{chain.extender_label}
                </span>
              )}
            </OptionTitle>
            {!chain.extender &&
              chain.chain_length === "14" &&
              chain.chain_figure !== "kid" && (
                <OptionTitle className="text-14 mt-0">
                  Choker Length
                </OptionTitle>
              )}
            <Divider
              className="bg-grey-10 mx-auto my-4"
              style={{ width: "50px" }}
            />
            <OptionPrice className="text-12 font-normal">
              {chain.price > 0 ? `+ ${to_currency(chain.price)}` : ""}
            </OptionPrice>
          </ChoiceOptionWrap>
        )
      })}
    </OptionsSlider>
  )
}
ChainLength.propTypes = {
  choices: PropTypes.object,
  inModal: PropTypes.bool,
  selectedChoice: PropTypes.object,
  selectedType: PropTypes.object,
  setChoice: PropTypes.func,
}
/**
 * Chain options
 * @param {Array} options from withWizard
 * @param {object} selectedChoice - from withWizard
 * @param {func} setChoice - from withWizard
 */
const ChainOptions = ({
  options,
  chainTypes,
  choices,
  groups,
  inModal,
  selectedChoice,
  selectedMetalFilter,
  setChoice,
  wizardData,
}) => {
  let defaultType = chainTypes[0]
  if (selectedChoice.value) {
    defaultType = wizardData.types.find(
      (t) => t.type === selectedChoice.chain_type
    )
  }
  const [selectedType, setType] = useState(defaultType)
  // Update initial selected chain type on metal change
  useEffect(() => {
    setType(defaultType)
  }, [selectedMetalFilter.value])

  useEffect(() => {
    if (selectedChoice.chain_type) {
      let selectedChain = chainTypes.find(
        (chain_type) => chain_type.type == selectedChoice.chain_type
      )
      setType(selectedChain ? selectedChain : chainTypes[0])
    }
  }, [selectedChoice.chain_type])

  return (
    <div
      className={cn("tablet:mr-0 tablet:px-0", inModal ? "-mr-16" : "px-12")}
    >
      {wizardData.chain_type_ui ? (
        <>
          <p className="body-sm text-black-print mb-16 font-semibold">
            PLEASE SELECT YOUR CHAIN TYPE:
          </p>
          <GradientContainer className="px-0">
            <FocusScope>
              <ChainType
                chainTypes={chainTypes}
                inModal={inModal}
                selectedMetalFilter={selectedMetalFilter}
                selectedType={selectedType}
                setChoice={setChoice}
                setType={setType}
              />
            </FocusScope>
          </GradientContainer>
          <Divider
            className={cn("bg-grey-10 mb-16 mr-16", inModal && "mt-10")}
          />
          <p className="body-sm text-black-print font-semibold">
            PLEASE SELECT YOUR CHAIN LENGTH:
          </p>
          {inModal && (
            <div className="bg-ruby/10 tablet:mt-6 mb-4 mr-16 mt-10 inline-block px-12 py-8">
              <p className="body-sm text-ruby font-semibold">
                Missing chain length! Please choose from one of the options
                below
              </p>
            </div>
          )}
          <GradientContainer className="px-0 pt-16">
            <FocusScope>
              <ChainLength
                choices={choices}
                inModal={inModal}
                selectedChoice={selectedChoice}
                selectedType={selectedType}
                setChoice={setChoice}
              />
            </FocusScope>
          </GradientContainer>
        </>
      ) : (
        <GradientContainer>
          {selectedChoice.error && (
            <div className="mb-8">
              <p className="text-12">{selectedChoice.error}</p>
            </div>
          )}
          {groups.length ? (
            groups.map((group, i) => (
              <div key={i}>
                <p className="body-sm text-black-print mb-16 font-semibold">
                  {group}
                </p>
                <OptionsSlider data-slider="true">
                  {options
                    .filter((opt) => opt.chain_group === group)
                    .map((opt) => {
                      const img = imageLookup("ch", opt.imageKey, "retina")
                      return (
                        <ChoiceOption
                          aria-checked={selectedChoice.value === opt.value}
                          className="basis-78"
                          data-cy={`product-wizard-option-chain-${opt.value}`}
                          data-is-option="true"
                          data-is-selected={selectedChoice.value === opt.value}
                          key={`chain-${opt.value}`}
                          onClick={() => setChoice(opt)}
                        >
                          <Image
                            height="40"
                            src={img.x2}
                            srcSet={img.x1 + " 1x," + img.x2 + " 2x"}
                            width="40"
                          />
                          <OptionTitle>{opt.title}</OptionTitle>
                          <OptionPrice>
                            {opt.price > 0 ? to_currency(opt.price) : ""}
                          </OptionPrice>
                        </ChoiceOption>
                      )
                    })}
                </OptionsSlider>
                {i !== groups.length - 1 && (
                  <Divider className="bg-grey-10 my-16 mr-16" />
                )}
              </div>
            ))
          ) : (
            <OptionsSlider data-slider="true">
              {options.map((opt) => {
                const img = imageLookup("ch", opt.imageKey, "retina")
                return (
                  <ChoiceOption
                    aria-checked={selectedChoice.value === opt.value}
                    className="basis-78"
                    data-cy={`product-wizard-option-chain-${opt.value}`}
                    data-is-option="true"
                    data-is-selected={selectedChoice.value === opt.value}
                    key={`chain-${opt.value}`}
                    onClick={() => setChoice(opt)}
                  >
                    <Image
                      alt={opt.title}
                      height="40"
                      src={img.x2}
                      srcSet={img.x1 + " 1x," + img.x2 + " 2x"}
                      width="40"
                    />
                    <OptionTitle>{opt.title}</OptionTitle>
                    <OptionPrice>
                      {opt.price > 0 ? to_currency(opt.price) : ""}
                    </OptionPrice>
                  </ChoiceOption>
                )
              })}
            </OptionsSlider>
          )}
        </GradientContainer>
      )}
    </div>
  )
}

const getQueryString = (state) => {
  let query = "?"
  Object.keys(state.choices)
    .filter((key) => !state.choices[key].disabled)
    .forEach((key) => {
      const choice = state.choices[key]
      query += `${choice.pref}=${encodeURI(choice.value)}&`
    })

  return query
}

const getSelectedMetalFilter = (state) => {
  if (typeof state.choices.sku !== "undefined") {
    return {
      pref: "sku",
      value: state.choices.sku.value,
    }
  } else if (typeof state.choices.m2 !== "undefined") {
    return {
      pref: "m2",
      value: state.choices.m2.value,
    }
  } else {
    return ""
  }
}

const getSelectedChoice = (state, ownProps) => {
  if (typeof state.choices[ownProps.pref] === "undefined") {
    return ""
  } else {
    return state.choices[ownProps.pref]
  }
}

const mapStateToProps = (state, ownProps) => {
  let options = []
  let groups = []
  let chainTypes = []
  let selectedMetalFilter = {}
  let wizardData = state.wizard.data[ownProps.pref]
  const adminMode = state.wizard.adminMode
  const header = state.wizard.header
  const historical = state.wizard.orderDate
    ? new Date(state.wizard.orderDate) <= new Date("2024-07-22")
    : false
  const external = state.wizard.external
  const choices = {
    ...state.choices,
    admin: {
      pref: "admin",
      value:
        ((adminMode || header.params_olid_present) && historical) || external
          ? "1"
          : "0",
    },
  }
  if (wizardData) {
    selectedMetalFilter = getSelectedMetalFilter(state)
    options = filterProductOptionsV2(wizardData.options, choices, true)
    chainTypes =
      filterProductOptionsV2(wizardData.types, choices, true)?.filter(
        (type) =>
          !wizardData.chain_type_ui ||
          filterProductOptionsV2(type.options || [], choices, true)?.length > 0
      ) || []
    groups = wizardData.groups
  }
  return {
    adminMode,
    chainTypes,
    choices,
    groups,
    openedPanel: state.panel.openedPanelIndexes.indexOf(ownProps.pref) > -1,
    options: options,
    query: getQueryString(state),
    selectedChoice: getSelectedChoice(state, ownProps),
    selectedMetalFilter,
    wizardData,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    setChoice: (data) => {
      dispatch(updateChoice(ownProps.pref, data, true))
      if (data.track_slide) {
        dispatch(trackSlide(data.track_slide))
      }
    },
  }
}

ChainOptions.propTypes = {
  adminMode: PropTypes.bool,
  chainTypes: PropTypes.array,
  choices: PropTypes.object,
  groups: PropTypes.array,
  inModal: PropTypes.bool,
  openedPanel: PropTypes.bool,
  options: PropTypes.array,
  query: PropTypes.string,
  selectedChoice: PropTypes.object,
  selectedMetalFilter: PropTypes.object,
  setChoice: PropTypes.func,
  wizardData: PropTypes.object,
}

export default connect(mapStateToProps, mapDispatchToProps)(ChainOptions)
